import { PatientListDto } from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { PatientsTable } from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import {
  activateTab,
  createTabInBackground,
  useTabs,
} from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  confirmAnonymizePatient,
  confirmArchivePatient,
  confirmPermanentlyDeletePatient,
  confirmRestorePatient,
} from '@lmt/unicorn-shared/app/features/patients/confirmations';

import styles from '../PatientListPage.module.scss';
import { useEventCallback } from '@mui/material';

interface Props {
  patients?: PatientListDto[];
  setSorting: (sorting: { sortBy?: string; desc?: boolean }) => void;
  isLoading: boolean;
  error?: unknown;
  isFiltered: boolean;
}

export const PatientList: FC<Props> = React.memo(function PatientList(props) {
  const i18next = useScopedTranslation('patients.list');
  const tabs = useTabs();
  const navigate = useNavigate();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const forms = useGlobalForms();

  const { patients, setSorting, isLoading } = props;

  const createPatientProfileTabInBackground = useEventCallback(
    (patientId: number) => {
      const patient = patients!.find((patient) => patient.id === patientId);
      if (patient) {
        return createTabInBackground(
          pages.patientProfile.link({ patientId }),
          dispatch,
        );
      }
      return null;
    },
  );

  const openPatientProfile = useEventCallback((patientId: number) => {
    const tab = createPatientProfileTabInBackground(patientId);
    if (tab) {
      activateTab(tab, navigate);
    }
  });

  const createDocument = useEventCallback((patientId: number) => {
    tabs.openTab(
      pages.createPatientDocument.link({
        patientId: patientId,
      }),
    );
  });

  const removePatientRq = useEventCallback(async (patientId: number) => {
    const reason = await confirmArchivePatient(modals);
    if (reason) {
      try {
        const removeDto = { reason };
        await ClientFactory.PatientClient.removePatient(patientId, removeDto);
        dispatch(NavigationActions.patientRemoved(patientId));
        toast(i18next.t('toasts.archive_successful'), {
          type: 'success',
        });
      } catch (e) {
        void modals.showError({ text: convertToErrorString(e) });
      }
    }
  });

  const removePatientPermanently = useEventCallback(
    async (patientId: number, patientName: string) => {
      const isConfirmed = await confirmPermanentlyDeletePatient(
        modals,
        patientName,
      );
      if (isConfirmed) {
        try {
          await ClientFactory.PatientClient.deletePermanently(patientId);
          dispatch(NavigationActions.patientRemoved(patientId));

          toast(i18next.t('toasts.delete_successful'), {
            type: 'success',
          });
        } catch (e) {
          void modals.showError({ text: convertToErrorString(e) });
        }
      }
    },
  );

  const anonymizePatient = useEventCallback(
    async (patientId: number, patientName: string) => {
      const reason = await confirmAnonymizePatient(modals, patientName);
      if (reason) {
        try {
          const removeDto = { reason };
          await ClientFactory.PatientClient.anonymize(patientId, removeDto);
          dispatch(NavigationActions.patientRemoved(patientId));
          toast(i18next.t('toasts.anonymize_successful'), {
            type: 'success',
          });
        } catch (e) {
          void modals.showError({ text: convertToErrorString(e) });
        }
      }
    },
  );

  const restorePatient = useEventCallback(async (patientId: number) => {
    const isConfirmed = await confirmRestorePatient(modals);
    if (isConfirmed) {
      await ClientFactory.PatientClient.restorePatient(patientId);
    }
  });

  return (
    <div className={styles.patientsTable}>
      <Loading
        loading={isLoading}
        title={i18next.t('loading')}
        className={styles.loading}
        error={props.error}
      >
        <PatientsTable
          data={patients ?? []}
          isNoPatientsFound={!isLoading && patients?.length === 0}
          isFiltered={props.isFiltered}
          openPatientProfile={openPatientProfile}
          createPatientProfileTab={createPatientProfileTabInBackground}
          removePatient={removePatientRq}
          removePatientPermanently={removePatientPermanently}
          anonymizePatient={anonymizePatient}
          restorePatient={restorePatient}
          changeSorting={setSorting}
          createPatient={() => forms.openPatientCreating()}
          createDocument={createDocument}
        />
      </Loading>
    </div>
  );
});
